import { useCallback, useEffect, useRef } from 'react';
import { useUser } from '@app/contexts/userContext';
import { useChatStore } from '@app/contexts/chatStoreContext';
import { idsMatch } from '@app/helpers/normalizeId';
import { chatSocketService } from '@app/services/chat-socket.service';

export function useWebSocketHandlers() {
  const { user: authUser } = useUser();
  const {
    updateState,
    removeOptimisticUpdate,
    chatStateRef,
    showNotification,
  } = useChatStore();

  // Socket event handlers
  const handleNewMessage = useCallback((messageData) => {
    if (process.env.NODE_ENV === 'development') {
      console.log('[Socket:recv] message:new', { uuid: messageData?.uuid, conversationId: messageData?.conversationId || messageData?.conversation_id, senderId: messageData?.sender_id || messageData?.senderId });
    }

    const conversationId = messageData?.conversationId || messageData?.conversation_id;
    const senderId = messageData?.sender_id || messageData?.senderId || messageData?.sender?.id;
    const activeConversationId = chatStateRef.current.activeConversation?.id;

    if (messageData.uuid) {
      removeOptimisticUpdate(messageData.uuid);
    }

    updateState(prev => {
      const isActiveConversation = idsMatch(activeConversationId, conversationId);

      if (isActiveConversation) {
        const filteredMessages = prev.conversationMessages.filter(
          msg => msg.uuid !== messageData.uuid
        );

        return {
          ...prev,
          conversationMessages: [...filteredMessages, messageData]
        };
      }

      const currentUnread = prev.conversationUnreadCounts?.[conversationId] || 0;

      return {
        ...prev,
        conversationUnreadCounts: {
          ...prev.conversationUnreadCounts,
          [conversationId]: currentUnread + 1
        }
      };
    });

    if (senderId && !idsMatch(senderId, authUser?.id)) {
      showNotification(
        messageData?.sender?.name || 'New message',
        messageData?.message || messageData?.content || 'You have a new message',
        messageData?.sender?.avatar?.thumb
      );
    }
  }, [updateState, removeOptimisticUpdate, chatStateRef, authUser?.id, showNotification]);

  const handleNewEventMessage = useCallback((messageData) => {
    // Remove optimistic update if it exists
    const tempId = `temp_${messageData.event_id}_${messageData.sender_id}`;
    removeOptimisticUpdate(tempId);

    updateState(prev => {
      // Remove any temporary messages and add the real one
      const filteredMessages = prev.eventChatMessages.filter(
        msg => !msg.id.toString().startsWith('temp_')
      );

      return {
        ...prev,
        eventChatMessages: [...filteredMessages, messageData]
      };
    });
  }, [updateState, removeOptimisticUpdate]);

  // O(1) duplicate detection for incoming socket messages
  const processedGroupMsgRef = useRef(new Set());

  const handleNewGroupMessage = useCallback((messageData) => {
    if (process.env.NODE_ENV === 'development') {
      console.log('[Socket:recv] group_message:new', { uuid: messageData?.uuid, groupChatId: messageData?.groupChatId || messageData?.group_chat_id, senderId: messageData?.sender_id || messageData?.senderId });
    }

    const messageTimestamp = messageData.createdAt || messageData.created_at || messageData.timestamp || new Date().toISOString();
    const groupChatId = messageData.group_chat_id || messageData.groupChatId || messageData.eventId || messageData.event_id;
    const senderId = messageData.sender_id || messageData.senderId || messageData.sender?.id;
    const activeGroupChatId = chatStateRef.current.activeGroupChat?.id;
    const isActiveGroup = idsMatch(activeGroupChatId, groupChatId);

    if (messageData.uuid) {
      removeOptimisticUpdate(messageData.uuid);
    }

    // Normalize the message structure
    const normalizedMessage = {
      id: messageData.id || messageData.uuid,
      uuid: messageData.uuid,
      message: messageData.message,
      type: messageData.type || 'TEXT',
      metadata: messageData.metadata,
      createdAt: messageTimestamp,
      sender: messageData.sender || {
        id: senderId,
        name: messageData.sender_name || 'Unknown',
        avatar: messageData.sender_avatar || null
      },
      groupChatId
    };

    // Fast-path O(1) duplicate check via ref Set
    const idKey = normalizedMessage.id != null ? String(normalizedMessage.id) : null;
    const uuidKey = normalizedMessage.uuid || null;

    if ((idKey && processedGroupMsgRef.current.has(idKey)) ||
        (uuidKey && processedGroupMsgRef.current.has(`u:${uuidKey}`))) {
      return;
    }

    // Track this message
    if (idKey) processedGroupMsgRef.current.add(idKey);
    if (uuidKey) processedGroupMsgRef.current.add(`u:${uuidKey}`);

    const isOwnMessage = idsMatch(senderId, authUser?.id);

    updateState((prevState) => {
      // Message belongs to a group the user is NOT currently viewing.
      // This includes the case where the user has no group chat open at all
      // (e.g. they're on another page). In every such case we must bump the
      // unread counter so the notification badge updates in real time.
      // NOTE: the previous `activeGroupChatId && !isActiveGroup` guard skipped
      // the increment whenever no group chat was active, which is exactly why
      // the badge never updated in the background.
      if (!isActiveGroup) {
        // Never count our own echoed messages, and never append them to the
        // (different) group chat the user currently has open.
        if (isOwnMessage) {
          return prevState;
        }

        const unreadCount = prevState.groupChatUnreadCounts?.[groupChatId] || 0;

        return {
          ...prevState,
          groupChatUnreadCounts: {
            ...prevState.groupChatUnreadCounts,
            [groupChatId]: unreadCount + 1
          }
        };
      }

      const existingMessages = prevState.groupChatMessages || [];

      // Atomic fallback check for messages loaded via API (not in the ref Set)
      const isDuplicate = existingMessages.some(
        (msg) => idsMatch(msg.id, normalizedMessage.id) ||
                 (msg.uuid && msg.uuid === normalizedMessage.uuid)
      );

      if (isDuplicate) return prevState;

      // Filter out optimistic message with same uuid, then append the real one
      const filtered = uuidKey
        ? existingMessages.filter((msg) => msg.uuid !== uuidKey)
        : existingMessages;

      return {
        ...prevState,
        groupChatMessages: [...filtered, normalizedMessage]
      };
    });

    // Show browser notification for new messages (not from current user)
    if (!idsMatch(senderId, authUser?.id)) {
      showNotification(
        `New message from ${normalizedMessage.sender.name}`,
        normalizedMessage.message,
        normalizedMessage.sender.avatar?.thumb
      );
    }
  }, [chatStateRef, removeOptimisticUpdate, updateState, authUser?.id, showNotification]);

  const handleNotificationNew = useCallback((payload) => {
    if (process.env.NODE_ENV === 'development') {
      console.log('[Socket:recv] notification:new', payload);
    }
    if (!payload) return;

    const conversationId = payload.conversationId || payload.conversation_id;
    const eventId = payload.eventId || payload.event_id || payload.groupChatId || payload.group_chat_id;

    // Skip counter increment if the user is currently viewing this conversation/group
    const activeConversationId = chatStateRef.current.activeConversation?.id;
    const activeGroupChatId = chatStateRef.current.activeGroupChat?.id;

    if (conversationId && idsMatch(activeConversationId, conversationId)) {
      return;
    }
    if (eventId && idsMatch(activeGroupChatId, eventId)) {
      return;
    }

    updateState(prev => {
      if (conversationId) {
        const currentUnread = prev.conversationUnreadCounts?.[conversationId] || 0;
        return {
          ...prev,
          conversationUnreadCounts: {
            ...prev.conversationUnreadCounts,
            [conversationId]: currentUnread + 1
          }
        };
      }

      if (eventId) {
        const currentUnread = prev.groupChatUnreadCounts?.[eventId] || 0;
        return {
          ...prev,
          groupChatUnreadCounts: {
            ...prev.groupChatUnreadCounts,
            [eventId]: currentUnread + 1
          }
        };
      }

      return prev;
    });
  }, [updateState, chatStateRef]);

  const handleTypingUpdate = useCallback((typingData) => {
    updateState(prev => ({
      ...prev,
      typingUsers: {
        ...prev.typingUsers,
        [`conversation_${typingData.conversationId}`]: typingData
      }
    }));
  }, [updateState]);

  const handleEventTypingUpdate = useCallback((typingData) => {
    updateState(prev => ({
      ...prev,
      typingUsers: {
        ...prev.typingUsers,
        [`event_${typingData.eventId}`]: typingData
      }
    }));
  }, [updateState]);

  const handleGroupTypingUpdate = useCallback((typingData) => {
    updateState(prev => ({
      ...prev,
      typingUsers: {
        ...prev.typingUsers,
        [`group_${typingData.groupChatId}`]: typingData
      }
    }));
  }, [updateState]);

  const handlePresenceUpdate = useCallback((presenceData) => {
    updateState(prev => {
      const newOnlineUsers = new Set(prev.onlineUsers);
      if (presenceData.status === 'online') {
        newOnlineUsers.add(presenceData.user_id);
      } else {
        newOnlineUsers.delete(presenceData.user_id);
      }
      return { ...prev, onlineUsers: newOnlineUsers };
    });
  }, [updateState]);

  const handleEventPresenceUpdate = useCallback((presenceData) => {
    // Handle event-specific presence updates
    handlePresenceUpdate(presenceData);
  }, [handlePresenceUpdate]);

  const handleGroupPresenceUpdate = useCallback((presenceData) => {
    // Handle group-specific presence updates
    handlePresenceUpdate(presenceData);
  }, [handlePresenceUpdate]);

  const handleEventParticipantUpdate = useCallback((participantData) => {
    if (participantData.action === 'joined') {
      updateState(prev => ({
        ...prev,
        eventChatParticipants: [...prev.eventChatParticipants, participantData.participant]
      }));
    } else if (participantData.action === 'left') {
      updateState(prev => ({
        ...prev,
        eventChatParticipants: prev.eventChatParticipants.filter(
          p => p.user_id !== participantData.participant.user_id
        )
      }));
    }
  }, [updateState]);

  const handleGroupParticipantUpdate = useCallback((participantData) => {
    if (participantData.action === 'joined') {
      updateState(prev => ({
        ...prev,
        groupChatMembers: [...prev.groupChatMembers, participantData.participant]
      }));
    } else if (participantData.action === 'left') {
      updateState(prev => ({
        ...prev,
        groupChatMembers: prev.groupChatMembers.filter(
          p => p.user_id !== participantData.participant.user_id
        )
      }));
    }
  }, [updateState]);

  // Event-driven connection status updates via chatSocketService listener
  useEffect(() => {
    const unsubscribe = chatSocketService.onStatusChange((newStatus) => {
      updateState((prev) => {
        if (prev.connectionStatus !== newStatus) {
          return {
            ...prev,
            connectionStatus: newStatus,
          };
        }
        return prev;
      });
    });

    return unsubscribe;
  }, [updateState]);

  // Typing indicators
  const startTyping = useCallback(async (type = 'conversation') => {
    await chatSocketService.startTyping(type);
  }, []);

  const stopTyping = useCallback(async (type = 'conversation') => {
    await chatSocketService.stopTyping(type);
  }, []);

  return {
    // Message handlers
    handleNewMessage,
    handleNewEventMessage,
    handleNewGroupMessage,
    handleNotificationNew,

    // Typing handlers
    handleTypingUpdate,
    handleEventTypingUpdate,
    handleGroupTypingUpdate,

    // Presence handlers
    handlePresenceUpdate,
    handleEventPresenceUpdate,
    handleGroupPresenceUpdate,
    handleEventParticipantUpdate,
    handleGroupParticipantUpdate,

    // Typing indicators
    startTyping,
    stopTyping
  };
}
